import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { finalize } from 'rxjs';

import type { CrmDigitalDocumentoStatusResponse, CrmDigitalDocumentoTipo, CrmDigitalSolicitacaoParams, CrmDigitalSolicitacaoResponse, CrmDigitalStatusResponse } from '@/core/api/sicom-api/registro/medico/crm-digital/crm-digital.model';
import { CrmDigitalService } from '@/core/api/sicom-api/registro/medico/crm-digital/crm-digital.service';
import { PessoaDomain } from '@/core/api/sicom-api/registro/pessoa/pessoa.domain';
import { AuthService } from '@/core/auth/auth.service';
import { CustomClass } from '@/shared/classes/custom.class';
import { CardContent } from '@/shared/components/card-content/card-content';
import { Empty } from '@/shared/components/layout/empty/empty';
import { Pesquisa } from '@/shared/components/pessoa/pesquisa/pesquisa';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectModule } from 'primeng/select';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

interface TipoRequisicaoOpcao {
    label: string;
    value: string;
}

interface MensagemArea {
    tipo: 'info' | 'success' | 'warn' | 'error';
    icone: string;
    titulo: string;
    descricao?: string;
    itens?: string[];
}

@Component({
    selector: 'app-documentos-medico',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, ReactiveFormsModule, ButtonModule, CheckboxModule, SelectModule, Pesquisa, Empty, CardContent, CanDirective],
    templateUrl: './documentos-medico.html',
    styleUrl: './documentos-medico.scss'
})
/** Emissão/validação de documentos (CRM digital) do médico. */
export class DocumentosMedico extends CustomClass {
    protected readonly roleEmissaoEmitir = PERMISSOES.medicos.emissaoValid.emitir;
    private readonly fb = inject(FormBuilder);
    private readonly destroyRef = inject(DestroyRef);
    private readonly crmDigitalService = inject(CrmDigitalService);
    private readonly authService = inject(AuthService);

    pesquisarDialog = false;
    carregandoStatus = signal(false);
    enviandoSolicitacao = signal(false);
    status: CrmDigitalStatusResponse | null = null;
    respostaSolicitacao: CrmDigitalSolicitacaoResponse | null = null;
    retorno: string | null = null;
    mensagensManuais: MensagemArea[] = [];
    private ultimaChaveStatus = '';
    private ultimaChavePessoa = '';

    readonly form = this.fb.nonNullable.group({
        emitirCim: [false],
        emitirCpm: [false],
        emitirCd: [false],
        tipoCarteira: ['1'],
        preSolicitacao: [false],
        tipoRequisicaoCim: [''],
        tipoRequisicaoCpm: [''],
        tipoRequisicaoCd: ['1'],
        motivoReimpressaoCim: [''],
        motivoReimpressaoCpm: [''],
        motivoReimpressaoCd: ['']
    });

    private readonly tipoCarteiraMedico: TipoRequisicaoOpcao = { label: 'Identidade Médica', value: '1' };
    private readonly tiposCarteiraConselheiro: Record<string, TipoRequisicaoOpcao> = {
        '2': { label: 'Conselheiro Regional', value: '2' },
        '3': { label: 'Conselheiro Federal', value: '3' }
    };

    private readonly labelsRequisicao: Record<string, string> = {
        '1': 'Primeira emissão',
        '2': 'Reimpressão',
        '3': 'Segunda via ou reemissão',
        '5': 'Pre-solicitação'
    };

    readonly motivosReimpressao: TipoRequisicaoOpcao[] = [
        { label: 'Sem alteração de dados', value: '8' },
        { label: 'Com alteração de dados', value: '9' }
    ];

    constructor() {
        super();

        this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.retorno = null;
            this.respostaSolicitacao = null;
            this.atualizarRequisicaoPreSolicitacao();
        });

        this.form.controls.preSolicitacao.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.atualizarRequisicaoPreSolicitacao();
        });

        effect(() => {
            const pessoa = this.pessoa();
            if (pessoa?.id && pessoa?.uf) {
                const chavePessoa = `${pessoa.id}-${pessoa.uf}`;
                if (chavePessoa !== this.ultimaChavePessoa) {
                    this.ultimaChavePessoa = chavePessoa;
                    this.limparEstadoCarteiraConselheiro();
                }
                this.consultarStatus();
                return;
            }

            this.status = null;
            this.retorno = null;
            this.respostaSolicitacao = null;
            this.ultimaChaveStatus = '';
            this.ultimaChavePessoa = '';
        });
    }

    get registroNaoSelecionado(): boolean {
        const pessoa = this.pessoa();
        return !pessoa?.id || !pessoa?.uf;
    }

    get tiposCarteira(): TipoRequisicaoOpcao[] {
        const opcoes = [this.tipoCarteiraMedico];
        const tipoConselheiro = this.medicoSelecionadoEhConselheiro ? this.status?.tipoCarteiraConselheiro : null;
        const opcaoConselheiro = tipoConselheiro ? this.tiposCarteiraConselheiro[tipoConselheiro] : null;
        return opcaoConselheiro ? [...opcoes, opcaoConselheiro] : opcoes;
    }

    get exibirTipoCarteira(): boolean {
        return this.tiposCarteira.length > 1;
    }

    private get medicoSelecionadoEhConselheiro(): boolean {
        return this.statusPertenceAoMedicoSelecionado() && !!this.status?.conselheiro && !!this.status?.tipoCarteiraConselheiro && !!this.tiposCarteiraConselheiro[this.status.tipoCarteiraConselheiro];
    }

    get mensagens(): MensagemArea[] {
        const mensagens: MensagemArea[] = [];

        if (this.carregandoStatus()) {
            mensagens.push({
                tipo: 'info',
                icone: 'pi pi-spin pi-spinner',
                titulo: 'Validando documentos',
                descricao: 'As pendências e a disponibilidade dos documentos estão sendo verificadas para o médico selecionado.'
            });
        } else if (this.status?.servicoOnline) {
            mensagens.push({
                tipo: 'success',
                icone: 'pi pi-check-circle',
                titulo: 'Serviço disponível',
                descricao: 'Consulta de solicitações realizada com sucesso.'
            });
        } else if (this.status) {
            mensagens.push({
                tipo: 'error',
                icone: 'pi pi-exclamation-triangle',
                titulo: 'Servidor remoto indisponível',
                itens: ['Não foi possível acessar o servidor remoto do CFM. Tente mais tarde.', 'Caso o erro persista, contate o administrador.']
            });
        } else {
            mensagens.push({
                tipo: 'info',
                icone: 'pi pi-info-circle',
                titulo: 'Aguardando validação',
                descricao: 'As pendências e a disponibilidade dos documentos serão verificadas automaticamente para o médico selecionado.'
            });
        }

        if (this.status) {
            const pendenciasCertificado = this.pendenciasDocumento('CD');
            mensagens.push({
                tipo: pendenciasCertificado.length > 0 ? 'warn' : 'success',
                icone: pendenciasCertificado.length > 0 ? 'pi pi-exclamation-triangle' : 'pi pi-check-circle',
                titulo: 'Certificado Digital',
                descricao: 'Validação de pendências para emissão.',
                itens: pendenciasCertificado.length > 0 ? pendenciasCertificado : ['Nenhuma pendência impeditiva para Certificado Digital.']
            });
        }

        if ((this.status?.pendencias?.length ?? 0) > 0) {
            mensagens.push({
                tipo: 'warn',
                icone: 'pi pi-exclamation-circle',
                titulo: 'Pendências gerais',
                descricao: 'Itens que podem bloquear a solicitação.',
                itens: this.status?.pendencias ?? []
            });
        }

        if (this.retorno) {
            mensagens.push({
                tipo: 'success',
                icone: 'pi pi-send',
                titulo: 'Retorno',
                descricao: this.retorno
            });
        }

        return [...mensagens, ...this.mensagensManuais];
    }

    abrirPesquisa = (): void => {
        this.pesquisarDialog = true;
    };

    selecionarPessoa(pessoa: PessoaDomain): void {
        this.carregarPessoa(pessoa);
        this.status = null;
        this.retorno = null;
        this.respostaSolicitacao = null;
        this.mensagensManuais = [];
        this.ultimaChaveStatus = '';
        this.ultimaChavePessoa = `${pessoa.id}-${pessoa.uf}`;
        this.form.patchValue({ tipoCarteira: '1' }, { emitEvent: false });
        this.consultarStatus();
    }

    consultarStatus(forcar = false, preservarRetorno = false): void {
        const pessoa = this.pessoa();
        if (!pessoa?.id || !pessoa?.uf) {
            this.status = null;
            this.ultimaChaveStatus = '';
            return;
        }

        const tipoCarteira = this.tipoCarteiraPermitida(this.form.controls.tipoCarteira.value) ? this.form.controls.tipoCarteira.value : '1';
        if (tipoCarteira !== this.form.controls.tipoCarteira.value) {
            this.form.patchValue({ tipoCarteira }, { emitEvent: false });
        }

        const chaveStatus = `${pessoa.id}-${pessoa.uf}-${tipoCarteira}`;
        if (!forcar && chaveStatus === this.ultimaChaveStatus) {
            return;
        }

        this.ultimaChaveStatus = chaveStatus;

        this.carregandoStatus.set(true);
        this.status = null;
        if (!preservarRetorno) {
            this.retorno = null;
            this.respostaSolicitacao = null;
        }
        this.mensagensManuais = [];

        this.crmDigitalService
            .consultarStatus({
                id: Number(pessoa.id),
                uf: pessoa.uf,
                tipoCarteira
            })
            .pipe(finalize(() => this.carregandoStatus.set(false)))
            .subscribe({
                next: (response) => {
                    if (!this.statusResponsePertenceAoMedicoSelecionado(response)) {
                        return;
                    }
                    this.status = response;
                    this.aplicarStatusNoFormulario(response);
                },
                error: (erro) => {
                    this.ultimaChaveStatus = '';
                    this.notificarErro(this.extrairMensagemErro(erro) ?? 'Não foi possível consultar o status dos documentos.');
                }
            });
    }

    enviarSolicitacao(): void {
        const pessoa = this.pessoa();
        if (!pessoa?.id || !pessoa?.uf || !this.status) {
            this.notificarErro('Consulte um médico antes de solicitar documentos.');
            return;
        }

        const formValue = this.form.getRawValue();
        if (!formValue.emitirCim && !formValue.emitirCpm && !formValue.emitirCd) {
            this.notificarErro('Selecione ao menos um documento.');
            return;
        }

        const params: CrmDigitalSolicitacaoParams = {
            id: Number(pessoa.id),
            uf: pessoa.uf,
            emitirCim: formValue.emitirCim,
            emitirCpm: formValue.emitirCpm,
            emitirCd: formValue.emitirCd,
            tipoCarteira: this.tipoCarteiraPermitida(formValue.tipoCarteira) ? formValue.tipoCarteira : '1',
            preSolicitacao: formValue.preSolicitacao,
            tipoRequisicaoCim: this.tipoRequisicaoSelecionada('CIM'),
            tipoRequisicaoCpm: this.tipoRequisicaoSelecionada('CPM'),
            tipoRequisicaoCd: formValue.emitirCd ? '1' : undefined,
            motivoReimpressaoCim: formValue.motivoReimpressaoCim,
            motivoReimpressaoCpm: formValue.motivoReimpressaoCpm,
            motivoReimpressaoCd: formValue.motivoReimpressaoCd,
            nomeOperador: this.nomeOperador(),
            cpfOperador: this.cpfOperador()
        };

        this.enviandoSolicitacao.set(true);
        this.retorno = null;
        this.respostaSolicitacao = null;
        this.mensagensManuais = [];
        this.crmDigitalService
            .enviarSolicitacao(params)
            .pipe(finalize(() => this.enviandoSolicitacao.set(false)))
            .subscribe({
                next: (response) => {
                    this.respostaSolicitacao = response ?? null;
                    this.retorno = response?.mensagem ?? response?.retorno ?? 'Solicitação enviada.';
                    this.consultarStatus(true, true);
                },
                error: (erro) => this.notificarErro(this.extrairMensagemErro(erro) ?? 'Não foi possível enviar a solicitação.')
            });
    }

    documento(tipo: CrmDigitalDocumentoTipo): CrmDigitalDocumentoStatusResponse | null {
        return this.status?.documentos?.find((item) => item.tipo === tipo) ?? null;
    }

    opcoesRequisicao(tipo: CrmDigitalDocumentoTipo): TipoRequisicaoOpcao[] {
        const opcoes = (this.documento(tipo)?.tiposRequisicao ?? []).map((value) => ({
            value,
            label: this.labelsRequisicao[value] ?? `Tipo ${value}`
        }));

        if (tipo === 'CIM' && this.form.controls.preSolicitacao.value && this.documento(tipo)?.habilitado && !opcoes.some((item) => item.value === '5')) {
            return [...opcoes, { value: '5', label: this.labelsRequisicao['5'] }];
        }

        return opcoes;
    }

    desabilitado(tipo: CrmDigitalDocumentoTipo): boolean {
        return !this.documento(tipo)?.habilitado || this.carregandoStatus() || this.enviandoSolicitacao();
    }

    pendenciasDocumento(tipo: CrmDigitalDocumentoTipo): string[] {
        return this.documento(tipo)?.pendencias ?? [];
    }

    get documentosSolicitados(): CrmDigitalDocumentoStatusResponse[] {
        return this.respostaSolicitacao?.documentos ?? [];
    }

    numeroDocumento(tipo: CrmDigitalDocumentoTipo): string | null {
        if (tipo === 'CIM') {
            return this.respostaSolicitacao?.numeroCarteira ?? null;
        }
        if (tipo === 'CPM') {
            return this.respostaSolicitacao?.numeroCpm ?? null;
        }
        return this.respostaSolicitacao?.numeroCertificado ?? null;
    }

    resultadoDocumentoDescricao(documento: CrmDigitalDocumentoStatusResponse): string {
        const numero = this.numeroDocumento(documento.tipo);
        if (numero) {
            return `Número emitido: ${numero}`;
        }
        if ((documento.pendencias?.length ?? 0) > 0) {
            return documento.pendencias.join(' ');
        }
        return documento.habilitado ? 'Solicitação registrada.' : 'Documento não habilitado no retorno.';
    }

    private aplicarStatusNoFormulario(status: CrmDigitalStatusResponse): void {
        const cim = status.documentos.find((item) => item.tipo === 'CIM');
        const cpm = status.documentos.find((item) => item.tipo === 'CPM');
        const cd = status.documentos.find((item) => item.tipo === 'CD');
        const tipoCarteiraAtual = this.form.controls.tipoCarteira.value;
        const tipoCarteiraPermitida = this.tipoCarteiraPermitida(tipoCarteiraAtual, status);

        this.form.patchValue(
            {
                tipoCarteira: tipoCarteiraPermitida ? tipoCarteiraAtual : '1',
                emitirCim: !!cim?.habilitado && this.form.controls.emitirCim.value,
                emitirCpm: !!cpm?.habilitado && this.form.controls.emitirCpm.value,
                emitirCd: !!cd?.habilitado && this.form.controls.emitirCd.value,
                tipoRequisicaoCim: this.valorRequisicao(cim, this.form.controls.tipoRequisicaoCim.value),
                tipoRequisicaoCpm: this.valorRequisicao(cpm, this.form.controls.tipoRequisicaoCpm.value),
                tipoRequisicaoCd: cd?.habilitado ? '1' : ''
            },
            { emitEvent: false }
        );
        this.atualizarRequisicaoPreSolicitacao();
    }

    private valorRequisicao(documento: CrmDigitalDocumentoStatusResponse | undefined, atual: string): string {
        const tipos = documento?.tiposRequisicao ?? [];
        if (atual && tipos.includes(atual)) {
            return atual;
        }
        return tipos[0] ?? '';
    }

    private tipoRequisicaoSelecionada(tipo: CrmDigitalDocumentoTipo): string | undefined {
        if (tipo === 'CIM' && this.form.controls.emitirCim.value) {
            return this.form.controls.tipoRequisicaoCim.value || this.opcoesRequisicao('CIM')[0]?.value;
        }
        if (tipo === 'CPM' && this.form.controls.emitirCpm.value) {
            return this.form.controls.tipoRequisicaoCpm.value || this.opcoesRequisicao('CPM')[0]?.value;
        }
        return undefined;
    }

    private tipoCarteiraPermitida(tipoCarteira: string, status = this.status): boolean {
        return tipoCarteira === '1' || (this.statusResponsePertenceAoMedicoSelecionado(status) && !!status?.conselheiro && tipoCarteira === status.tipoCarteiraConselheiro && !!this.tiposCarteiraConselheiro[tipoCarteira]);
    }

    private statusPertenceAoMedicoSelecionado(): boolean {
        return this.statusResponsePertenceAoMedicoSelecionado(this.status);
    }

    private statusResponsePertenceAoMedicoSelecionado(status: CrmDigitalStatusResponse | null | undefined): boolean {
        const pessoa = this.pessoa();
        return !!status && !!pessoa?.id && !!pessoa?.uf && Number(pessoa.id) === Number(status.id) && String(pessoa.uf).toUpperCase() === String(status.uf).toUpperCase();
    }

    private limparEstadoCarteiraConselheiro(): void {
        this.status = null;
        this.retorno = null;
        this.respostaSolicitacao = null;
        this.mensagensManuais = [];
        this.ultimaChaveStatus = '';
        this.form.patchValue({ tipoCarteira: '1' }, { emitEvent: false });
    }

    private nomeOperador(): string | undefined {
        const claims = this.claimsUsuario();
        return String(claims?.['name'] ?? claims?.['preferred_username'] ?? '').trim() || undefined;
    }

    private cpfOperador(): string | undefined {
        const claims = this.claimsUsuario();
        const attributes = claims?.['attributes'] as Record<string, unknown> | undefined;
        const login = claims?.['login'] as Record<string, unknown> | undefined;
        const loginIdentifier = String(login?.['type'] ?? '').toLowerCase() === 'cpf' ? login?.['identifier'] : '';
        const cpf = String(attributes?.['cpf'] ?? claims?.['cpf'] ?? claims?.['document'] ?? loginIdentifier ?? '').replace(/\D/g, '');
        return cpf || undefined;
    }

    private atualizarRequisicaoPreSolicitacao(): void {
        const opcoesCim = this.opcoesRequisicao('CIM').map((item) => item.value);
        const requisicaoAtual = this.form.controls.tipoRequisicaoCim.value;

        if (this.form.controls.preSolicitacao.value && this.form.controls.emitirCim.value && opcoesCim.includes('5') && requisicaoAtual !== '5') {
            this.form.patchValue({ tipoRequisicaoCim: '5' }, { emitEvent: false });
            return;
        }

        if (!this.form.controls.preSolicitacao.value && requisicaoAtual === '5') {
            this.form.patchValue({ tipoRequisicaoCim: opcoesCim.find((item) => item !== '5') ?? '' }, { emitEvent: false });
        }
    }

    private claimsUsuario(): Record<string, unknown> {
        return {
            ...(this.authService.decodeToken() ?? {}),
            ...(this.authService.identityClaims ?? {})
        };
    }

    private notificarErro(detalhe: string): void {
        this.mensagensManuais = [
            {
                tipo: 'error',
                icone: 'pi pi-exclamation-triangle',
                titulo: 'Erro',
                descricao: detalhe
            }
        ];
    }

    private extrairMensagemErro(erro: unknown): string | null {
        const e = erro as { error?: { errors?: string[]; detail?: string; message?: string }; message?: string };
        const errors = e?.error?.errors;
        if (Array.isArray(errors) && errors.length > 0) {
            return errors.join(' ');
        }
        return e?.error?.detail ?? e?.error?.message ?? e?.message ?? null;
    }
}
